<script setup>
/* eslint-disable */
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { applyThemeClasses } from '@/utils/themeColors'

const props = defineProps({
  results: {
    type: Array,
    required: true
  },
  racingChocobos: {
    type: Array,
    required: true
  },
  raceLength: {
    type: String,
    required: true,
    validator: (value) => ['short', 'medium', 'long'].includes(value)
  },
  circuit: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['complete'])

// Add all required refs
const raceStarted = ref(false)
const racePositions = ref({})
const verticalPositions = ref({})
const spriteFrames = ref({})
const trackPosition = ref(0)
const validResults = ref([])
const backgroundPosition = ref(0)
const imageCache = ref(new Map())
const chocoboSpeeds = ref({})
const finishedChocobos = ref({})
const currentLeader = ref(null)
const raceStartTime = ref(0)

let animationFrame = null
let lastFrameChange = 0

const START_X = 5    
const FINISH_X = 90  

const assignRandomVerticalPositions = (racers) => {
  const positions = racers.map((_, index) => 55 + (index * 4))
  const shuffledPositions = [...positions].sort(() => Math.random() - 0.5)
  
  racers.forEach((result) => {
    verticalPositions.value[result.chocobo.id] = shuffledPositions.pop()
  })
}

const racingPatterns = {
  // Initial burst pattern (0-20% of race)
  EARLY_SPRINT: {
    start: 0,
    end: 0.5,
    baseMultiplier: 2.8
  },
  // Mid-race steady running (20-70% of race)
  MID_RACE: {
    start: 0.5,
    end: 0.6,
    baseMultiplier: 1.0
  },
  // Final push (70-100% of race)
  FINAL_SPRINT: {
    start: 0.6,
    end: 1.0,
    baseMultiplier: 2.3
  }
}

const generateRunnerPattern = (result) => {
  const chocobo = result.chocobo
  const speedDifference = chocobo.speed - chocobo.stamina
  const staminaScore = chocobo.stamina / 150 // Normalized stamina
  
  // Determine if this chocobo should be a false leader based on speed > stamina
  const isFalseLeader = (
    props.raceLength !== 'short' && // Only in medium/long races
    speedDifference > 30 && // Speed significantly higher than stamina
    chocobo.speed > 80 && // Must have decent speed to lead early
    result.position > 3 && // Will NOT win the race
    Math.random() < 0.6 // 60% chance for dramatic effect
  )
  
  // Create dramatic false leader pattern
  if (isFalseLeader) {
    const speedAdvantage = Math.min(speedDifference / 50, 1.0) // 0 to 1.0
    return {
      early: 1.6 + (speedAdvantage * 0.4), // Fast early but not teleporting (1.6-2.0x)
      mid: 0.4 + (staminaScore * 0.1), // Slowing down gradually (0.4-0.5x)
      final: 0.1 + (staminaScore * 0.05), // Almost stopped (0.1-0.15x)
      isFalseLeader: true,
      speedAdvantage: speedAdvantage
    }
  }
  
  // True winner who starts slow but has stamina
  if (result.position === 1 && staminaScore > 0.6) {
    return {
      early: 0.7 + (Math.random() * 0.2), // Conservative start
      mid: 1.1 + (staminaScore * 0.2), // Building momentum
      final: 1.4 + (staminaScore * 0.4), // Strong finish
      isFalseLeader: false
    }
  }
  
  // High stamina tactical runner
  if (staminaScore > 0.7 && chocobo.stamina > chocobo.speed) {
    return {
      early: 0.8 + (Math.random() * 0.1),
      mid: 1.0 + (staminaScore * 0.1),
      final: 1.2 + (staminaScore * 0.3),
      isFalseLeader: false
    }
  }
  
  // Balanced runner
  return {
    early: 0.9 + (Math.random() * 0.2),
    mid: 1.0 + (Math.random() * 0.1),
    final: 1.0 + (staminaScore * 0.2),
    isFalseLeader: false
  }
}


const calculateTimeBasedPosition = (progress, result, runnerPattern, elapsedTime) => {
  // Get or initialize previous position for smooth movement
  const chocoboId = result.chocobo.id
  const previousPosition = racePositions.value[chocoboId] || START_X
  
  // Base progress calculation
  const raceProgress = elapsedTime / raceConfig.value.duration
  
  // Apply running pattern to create dynamic movement
  let speedMultiplier = 1.0
  
  if (raceProgress <= 0.25) {
    // Early race - apply early pattern
    speedMultiplier = runnerPattern.early
  } else if (raceProgress <= 0.75) {
    // Mid race - apply mid pattern with stamina effects
    speedMultiplier = runnerPattern.mid
    
    // Gradual stamina slowdown for false leaders
    if (runnerPattern.isFalseLeader) {
      const fadeStart = 0.2
      const fadeProgress = Math.max(0, (raceProgress - fadeStart) / (0.8 - fadeStart))
      const fadeIntensity = Math.min(fadeProgress, 1.0)
      // Gradual slowdown rather than sudden collapse
      speedMultiplier = Math.max(0.05, speedMultiplier * (1.0 - (fadeIntensity * 0.9)))
    }
  } else {
    // Final sprint - apply final pattern
    speedMultiplier = runnerPattern.final
    
    // Continue the slowdown for false leaders
    if (runnerPattern.isFalseLeader) {
      speedMultiplier = Math.max(0.02, speedMultiplier * 0.1) // Nearly stopped
    }
  }
  
  // Calculate movement delta based on speed
  const baseMovementPerFrame = (FINISH_X - START_X) / (raceConfig.value.duration / 16.67) // 60fps
  const actualMovement = baseMovementPerFrame * speedMultiplier
  
  // Calculate new position by adding movement to previous position
  let newPosition = previousPosition + actualMovement
  
  // Apply some variance based on final position for eventual convergence
  const winnerTime = validResults.value[0].finishTime
  const timeDifference = result.finishTime - winnerTime
  const maxTimeDiff = validResults.value[validResults.value.length - 1].finishTime - winnerTime
  
  // Very subtle position adjustment towards final result (only 10% influence)
  if (raceProgress > 0.7) {
    const finalPositionInfluence = (raceProgress - 0.7) / 0.3 // 0 to 1 over final 30%
    const targetGap = (timeDifference / maxTimeDiff) * 15 // Maximum 15% gap
    const targetPosition = FINISH_X - targetGap
    const positionAdjustment = (targetPosition - newPosition) * finalPositionInfluence * 0.1
    newPosition += positionAdjustment
  }
  
  // Store current speed for visual effects
  chocoboSpeeds.value[chocoboId] = speedMultiplier
  
  // Check if finished (when they cross the line)
  if (newPosition >= FINISH_X && !finishedChocobos.value[chocoboId]) {
    finishedChocobos.value[chocoboId] = true
    return FINISH_X
  }
  
  return Math.min(FINISH_X, Math.max(START_X, newPosition))
}


const FRAME_DURATION = computed(() => {
  switch(props.raceLength) {
    case 'short': return 80
    case 'long': return 120
    default: return 100
  }
})

const raceConfig = computed(() => {
  // Base the duration on actual winner's time with faster pacing
  const winnerTime = validResults.value.length > 0 ? validResults.value[0].finishTime : 60
  
  switch(props.raceLength) {
    case 'short':
      return {
        duration: 6000, // 8 seconds for sprint
        scrollSpeed: 0.15,
        trackSpeed: 0.9,
        bgScroll: 0.1
      }
    case 'long':
      return {
        duration: 14000, // 16 seconds for endurance
        scrollSpeed: 0.15,
        trackSpeed: 0.5,
        bgScroll: 0.09
      }
    default:
      return {
        duration: 10000, // 12 seconds for medium
        scrollSpeed: 0.15,
        trackSpeed: 0.6,
        bgScroll: 0.1
      }
  }
})

const getCircuitAssetSuffix = computed(() => {
  return props.circuit?.id || 'regional'
})

// Helper functions for visual effects
const getSpeedBlur = (chocoboId) => {
  const speed = chocoboSpeeds.value[chocoboId] || 1.0
  if (speed > 1.3) return 'blur(0.5px)'
  if (speed > 1.1) return 'blur(0.3px)'
  return 'none'
}

const getCurrentGap = (chocoboId) => {
  if (!currentLeader.value || chocoboId === currentLeader.value) return ''
  
  const leaderPos = racePositions.value[currentLeader.value] || 0
  const chocoboPos = racePositions.value[chocoboId] || 0
  const gap = leaderPos - chocoboPos
  
  if (gap < 2) return 'close'
  if (gap < 5) return '-' + Math.floor(gap/2) + 'L'
  if (gap < 10) return '-' + Math.floor(gap/3) + 'L'
  return '-' + Math.floor(gap/4) + 'L'
}

const getPositionClass = (position) => {
  if (position === 1) return 'text-yellow-500'
  if (position === 2) return 'text-gray-400'
  if (position === 3) return 'text-amber-600'
  return 'text-gray-600'
}

const getCurrentRaceOrder = () => {
  if (!raceStarted.value) return validResults.value
  
  // Sort by current position (highest first = leading)
  return validResults.value.slice().sort((a, b) => {
    const posA = racePositions.value[a.chocobo.id] || 0
    const posB = racePositions.value[b.chocobo.id] || 0
    return posB - posA
  })
}

onMounted(() => {
  validResults.value = props.results.filter(result => 
  props.racingChocobos.some(chocobo => chocobo.id === result.chocobo.id))
  const colors = ['yellow', 'pink', 'black', 'gold', 'blue', 'green', 'white', 'red']
  const frames = [0, 1, 2]

  colors.forEach(color => {
    frames.forEach(frame => {
      const key = `${color}_sprite_${frame}`
      imageCache.value.set(key, require(`@/assets/${color}_sprite_${frame}.png`))
    })
  })

  imageCache.value.set('race_track', require(`@/assets/race_track_${getCircuitAssetSuffix.value}.png`))
  imageCache.value.set('race_track_bg', require(`@/assets/race_track_bg_${getCircuitAssetSuffix.value}.png`))
})

onBeforeUnmount(() => {
  if (animationFrame) {
    cancelAnimationFrame(animationFrame)
  }
})

const startRace = () => {
  raceStarted.value = true
  raceStartTime.value = performance.now()
  
  // Sort results by finish time to ensure proper ordering
  validResults.value.sort((a, b) => a.finishTime - b.finishTime)
  
  // Generate unique running patterns for each racer
  const runnerPatterns = {}
  let falseLeaderCount = 0
  
  validResults.value.forEach(result => {
    const pattern = generateRunnerPattern(result)
    // Limit false leaders to 1-2 per race
    if (pattern.isFalseLeader && falseLeaderCount < 2) {
      falseLeaderCount++
    } else if (pattern.isFalseLeader) {
      // Convert excess false leaders to normal patterns
      pattern.isFalseLeader = false
      pattern.early = 1.0
      pattern.mid = 0.9
      pattern.final = 0.8
    }
    runnerPatterns[result.chocobo.id] = pattern
    spriteFrames.value[result.chocobo.id] = 0
    racePositions.value[result.chocobo.id] = START_X
    chocoboSpeeds.value[result.chocobo.id] = 1.0
  })
  
  assignRandomVerticalPositions(validResults.value)

  const animate = (currentTime) => {
    const elapsed = currentTime - raceStartTime.value
    const progress = Math.min(elapsed / raceConfig.value.duration, 1)

    trackPosition.value = (elapsed * raceConfig.value.trackSpeed) % 1850
    backgroundPosition.value = (elapsed * raceConfig.value.bgScroll) % 1850

    // Update sprite frames
    if (currentTime - lastFrameChange > FRAME_DURATION.value) {
      validResults.value.forEach(result => {
        if (!finishedChocobos.value[result.chocobo.id]) {
          spriteFrames.value[result.chocobo.id] = (spriteFrames.value[result.chocobo.id] + 1) % 3
        }
      })
      lastFrameChange = currentTime
    }

    // Update positions using time-based system
    validResults.value.forEach((result) => {
      const position = calculateTimeBasedPosition(
        progress,
        result,
        runnerPatterns[result.chocobo.id],
        elapsed
      )
      racePositions.value[result.chocobo.id] = position
    })
    
    // Track current leader for display
    let leaderId = null
    let maxPosition = -1
    validResults.value.forEach(result => {
      if (racePositions.value[result.chocobo.id] > maxPosition) {
        maxPosition = racePositions.value[result.chocobo.id]
        leaderId = result.chocobo.id
      }
    })
    currentLeader.value = leaderId

    if (progress < 1) {
      animationFrame = requestAnimationFrame(animate)
    } else {
      setTimeout(() => emit('complete'), 1500)
    }
  }

  animationFrame = requestAnimationFrame(animate)
}
</script>

<template>
  <div :class="`fixed inset-0 ${applyThemeClasses('components.modal.overlay')} flex items-center justify-center z-50`">
    <div :class="`rounded-lg ${applyThemeClasses('components.modal.container')} w-full max-w-[650px] p-6`">
      <div class="mb-4 flex justify-between items-center">
        <h2 :class="applyThemeClasses('text.primary')">Race in Progress</h2>
        <button 
          v-if="!raceStarted"
          @click="startRace"
          class="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
        >
          Start Race
        </button>
      </div>
      
      <!-- Position indicators -->
      <div v-if="raceStarted" class="absolute top-2 right-2 bg-black/80 text-white p-2 rounded text-xs z-10">
        <div class="font-bold mb-1">Live Positions</div>
        <div v-for="(result, idx) in getCurrentRaceOrder().slice(0, 5)" :key="`pos-${result.chocobo.id}`">
          <span :class="getPositionClass(idx + 1)">{{ idx + 1 }}.</span>
          {{ result.chocobo.name }}
          <span v-if="idx > 0" class="text-gray-400 ml-1">{{ getCurrentGap(result.chocobo.id) }}</span>
        </div>
      </div>
      
      <div class="relative h-96 rounded-lg overflow-hidden">
        <div
          class="absolute bottom-0 left-0"
          :style="{
            backgroundImage: `url(${imageCache.get('race_track')})`,
            backgroundRepeat: 'no-repeat',
            height: '240px',
            width: '1509px',
            transform: `translateX(-${backgroundPosition * raceConfig.trackSpeed}px)`,
            transition: 'transform 50ms linear',
            zIndex: 1
          }"
        ></div>
        
        <div
          class="absolute left-0 right-0"
          :style="{
            backgroundImage: `url(${imageCache.get('race_track_bg')})`,
            backgroundRepeat: 'repeat-x',
            height: '125px',
            backgroundPosition: `-${backgroundPosition}px 0px`,
            transition: 'background-position 50ms linear',
            bottom: '180px',
            zIndex: 0
          }"
        ></div>
        
        <div 
          v-for="(result, index) in validResults"
          :key="`label-${result.chocobo.id}`"
          class="absolute transition-all duration-50"
          :style="{
            left: `${racePositions[result.chocobo.id]}%`,
            top: raceStarted ? `${verticalPositions[result.chocobo.id]}%` : `${55 + (index * 4)}%`,
            transform: 'translateX(-50%)',
            zIndex: 2
          }"
        >
          <div 
            class="relative top-8 bg-black/70 text-white px-2 py-0.5 rounded-full text-xs whitespace-nowrap"
          >
            {{ result.chocobo.name }}
          </div>
        </div>
        
        <!-- Racing Chocobos Layer (above labels) -->
        <div 
          v-for="(result, index) in validResults"
          :key="`sprite-${result.chocobo.id}`"
          class="absolute w-20 h-12 transition-all duration-50"
          :style="{
            left: `${racePositions[result.chocobo.id]}%`,
            top: raceStarted ? `${verticalPositions[result.chocobo.id]}%` : `${55 + (index * 4)}%`,
            zIndex: 3,
            filter: getSpeedBlur(result.chocobo.id)
          }"
        >
          <img 
            :src="imageCache.get(`${result.chocobo.color}_sprite_${spriteFrames[result.chocobo.id] || 0}`)"
            :alt="result.chocobo.name"
            class="w-full h-full object-contain"
            :class="{ 'opacity-80': finishedChocobos[result.chocobo.id] }"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
* {
  image-rendering: pixelated;
}
</style>